import React, { useState, ChangeEvent, useEffect, KeyboardEvent } from 'react';
import { Modal, Form, Button as BootstrapButton } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import SearchBox from 'components/common/SearchBox';
import {
  HVAScorecardsTable,
  hvascorecardsTableColumns
} from '../../components/tables/HVAScorecardsTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import {
  fetchHVAScorecards,
  HVAScorecard,
  HVAScorecardQuestion,
  createHVAScorecard
} from 'services/hvascorecard.service';
import Button from 'components/base/Button';
import ScorecardQuestionsList from './ScorecardQuestionsList';

const defaultFormData: HVAScorecard = {
  id: 0,
  name: '',
  systemdefault: false,
  active: true,
  production: false,
  questions: []
};

const ScorecardList: React.FC = () => {
  const [hvascorecards, setHVAScorecards] = useState<HVAScorecard[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState<HVAScorecard>({
    id: 0,
    name: '',
    systemdefault: false,
    active: true,
    production: false,
    questions: []
  });

  const loadScorecards = async () => {
    try {
      const hvascorecardsData = await fetchHVAScorecards();
      setHVAScorecards(hvascorecardsData);
    } catch (error) {
      console.error('Error fetching scorecards:', error);
    }
  };

  useEffect(() => {
    loadScorecards();
  }, []);

  const table = useAdvanceTable({
    data: hvascorecards,
    columns: hvascorecardsTableColumns(loadScorecards),
    pageSize: 1,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const handleSearchKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleShowModal = () => {
    setFormData(defaultFormData);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setFormData(defaultFormData);
    setShowModal(false);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleQuestionsChange = (questions: HVAScorecardQuestion[]) => {
    setFormData({
      ...formData,
      questions
    });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createHVAScorecard(formData);
      await loadScorecards(); // Recargar los scorecards
      handleCloseModal();
    } catch (error) {
      alert('Error creating new scorecard');
      handleCloseModal();
    }
  };

  return (
    <div>
      <div className="mb-4">
        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex flex-wrap gap-3">
              <SearchBox
                placeholder="Search Scorecards"
                onChange={handleSearchInputChange}
                onKeyDown={handleSearchKeyPress}
              />
              <div className="ms-auto order-2">
                <Button
                  variant="primary"
                  className="p-2"
                  onClick={handleShowModal}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2 fs-8" />
                  Create New Scorecard
                </Button>
              </div>
            </div>
          </div>
          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-translucent position-relative top-1">
            <HVAScorecardsTable />
          </div>
        </AdvanceTableProvider>
      </div>

      <Modal show={showModal} onHide={handleCloseModal} centered size="xl">
        <Form onSubmit={handleFormSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Create New Scorecard</Modal.Title>
          </Modal.Header>
          <Modal.Body className="px-6">
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
              <div className="form-check form-switch mb-0 pt-2">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    name="systemdefault"
                    value="systemdefault"
                    checked={formData.systemdefault}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  To be used as the Default scorecard for a campaign
                </label>
              </div>
              <div className="form-check form-switch mb-0">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    name="production"
                    value="production"
                    checked={formData.production}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  This scorecard is in Production mode
                </label>
              </div>
              <div className="form-check form-switch pb-3">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    name="active"
                    value="active"
                    checked={formData.active}
                    onChange={handleInputChange}
                    className="form-check-input"
                  />
                  This scorecard is Active
                </label>
              </div>
            </Form.Group>
            <ScorecardQuestionsList
              questions={formData.questions}
              onQuestionsChange={handleQuestionsChange}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <BootstrapButton variant="primary" type="submit">
              Create Scorecard
            </BootstrapButton>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default ScorecardList;
